
import React, { JSX, useState } from 'react'
import {
  YOUTUBE_TERMS_URL,
  GOOGLE_PRIVACY_URL,
  GOOGLE_SECURITY_SETTINGS_URL,
  PRIVACY_POLICY_URL
} from 'lib/externalLinks'

const ExternalLink = ({ href, children }: { href: string, children: React.ReactNode }): JSX.Element => (
  <a
    href={href}
    target='_blank'
    rel='noopener noreferrer'
    className='font-medium text-brand underline hover:text-brand/80'
  >
    {children}
  </a>
)

const SectionHeading = ({ children }: { children: React.ReactNode }): JSX.Element => (
  <h3 className='mt-4 text-xs font-semibold uppercase tracking-wider text-dm-text-accent'>
    {children}
  </h3>
)

export const YouTubeConsentDialog = ({
  onAgree,
  onClose,
  busy
}: {
  onAgree: () => void
  onClose: () => void
  busy: boolean
}): JSX.Element => {
  const [agreed, setAgreed] = useState(false)
  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4'
      data-testid='youtube-consent-dialog'
    >
      <div className='max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-white/10 bg-dm-bg-secondary p-6 shadow-2xl'>
        <h2 className='text-lg font-semibold text-dm-text-primary'>
          Before you connect YouTube
        </h2>
        <p className='mt-2 text-sm text-dm-text-secondary'>
          FlightDeck by StagePilot uses YouTube API Services to power YouTube
          live-chat features. Please review the following before connecting
          your account.
        </p>

        <SectionHeading>What we access</SectionHeading>
        <ul className='mt-2 list-disc space-y-2 pl-5 text-sm text-dm-text-secondary'>
          <li>
            Live chat messages on your channel, read through the official
            YouTube Data API, and only while a chat session you started is
            running. We never read your chat in the background.
          </li>
          <li>
            Your channel&apos;s public identity (name, channel ID, and
            thumbnail), so we can show which channel is connected.
          </li>
        </ul>

        <SectionHeading>How we handle it</SectionHeading>
        <ul className='mt-2 list-disc space-y-2 pl-5 text-sm text-dm-text-secondary'>
          <li>
            YouTube chat messages are processed in real time and are not
            stored. Anything derived from YouTube data is kept only briefly.
            See our Privacy Policy for more information.
          </li>
          <li>
            You can disconnect at any time on the Connections page, which
            immediately revokes our access with Google and deletes the stored
            authorization. You can also revoke access yourself in your{' '}
            <ExternalLink href={GOOGLE_SECURITY_SETTINGS_URL}>Google security settings</ExternalLink>.
          </li>
        </ul>

        <SectionHeading>Your agreement</SectionHeading>
        <ul className='mt-2 list-disc space-y-2 pl-5 text-sm text-dm-text-secondary'>
          <li>
            By connecting, you agree to be bound by the{' '}
            <ExternalLink href={YOUTUBE_TERMS_URL}>YouTube Terms of Service</ExternalLink>.
          </li>
          <li>
            The{' '}
            <ExternalLink href={GOOGLE_PRIVACY_URL}>Google Privacy Policy</ExternalLink>{' '}
            applies to Google&apos;s handling of your data.
          </li>
          <li>
            Our{' '}
            <ExternalLink href={PRIVACY_POLICY_URL}>Privacy Policy</ExternalLink>{' '}
            describes the YouTube data we handle.
          </li>
          <li>
            Per the StagePilot Terms of Use, YouTube features may not be used
            on streams featuring adult, gambling, violent, or hateful content.
          </li>
        </ul>

        <label className='mt-5 flex cursor-pointer items-start gap-2.5 rounded-md border border-white/10 bg-dm-bg-accent/30 p-3 text-sm text-dm-text-primary'>
          <input
            type='checkbox'
            checked={agreed}
            onChange={(e) => setAgreed(e.target.checked)}
            disabled={busy}
            data-testid='youtube-consent-checkbox'
            className='mt-0.5 size-4 shrink-0 rounded border-white/20 bg-dm-bg-accent/60 text-brand focus:ring-brand'
          />
          <span>I agree to the YouTube Terms of Service and the terms above.</span>
        </label>

        <div className='mt-5 flex items-center justify-end gap-2'>
          <button
            type='button'
            onClick={onClose}
            disabled={busy}
            className='rounded-md border border-white/10 px-4 py-2 text-sm font-medium text-dm-text-secondary hover:border-white/20 hover:text-dm-text-primary disabled:cursor-not-allowed disabled:opacity-60'
          >
            Not now
          </button>
          <button
            type='button'
            onClick={onAgree}
            disabled={!agreed || busy}
            data-testid='youtube-consent-agree'
            className='rounded-md bg-brand px-4 py-2 text-sm font-semibold text-black hover:bg-brand/80 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {busy ? 'Recording consent…' : 'Agree and connect YouTube'}
          </button>
        </div>
      </div>
    </div>
  )
}
